import { decodeToken } from "react-jwt";
import { FullSmartheartUser } from "../api/index.defs";
import { Constants, ErrorConstants, RouteConstants } from "../constants";
import {
  ICreateAuthenticatedUser,
  LogoutReason,
} from "../models/authenticated-user.interface";
import { Permissions } from "../models/permission";
import { LoginFailureReason } from "../models/results/login-result.interface";
import Role from "../models/role";
import { IToken, ITokenUser } from "../models/token.interface";
import { OrgType } from "../models/users.interface";
import LocalStorageUtil from "./localStorage";
import logger from "./logger";

export const getRoleFromServerRoleAndOrg = (
  id: number,
  organizationId?: number
): Role => {
  /**
   * == Non-Pro ==
   * 5 = NonProfessionalStandalone,
   * == Org Non-Pro ==
   * 7 = NonProfessionalOrganization,
   * == Pro ==
   * 6 = ProfessionalStandalone,
   * == Org-Pro ==
   * 8 = ProfessionalOrganization,
   * == Org Admin ==
   * 9 = ProfessionalOrganizationAdmin,
   * == Reviewer ==
   * 10 = ProfessionalEcgReviewer
   * == Consultant ==
   * 11 = Consultant
   * == Undefined ==
   * 1 = Patient (Legacy),
   * 2 = Physician (Legacy),
   * 3 = Nurse (Legacy),
   * 4 = ApiUser (Legacy),
   */
  switch (id) {
    case 5:
      return Role.NonProfessional;
    case 7:
      return Role.OrgNonProfessional;
    case 6:
      return Role.Professional;
    case 8:
      return organizationId === OrgType.Standalone
        ? Role.Professional
        : Role.OrgProfessional;
    case 9:
      return Role.OrgAdmin;
    case 10:
      return Role.Reviewer;
    case 11:
      return Role.Consultant;
    default:
      throw new Error(
        `Error converting Server Role ID to App Role. Invalid Server RoleId ${id}`
      );
  }
};

export const getServerRoleFromRole = (role: Role): number => {
  /**
   * == Non-Pro ==
   * 5 = NonProfessionalStandalone,
   * == Org Non-Pro ==
   * 7 = NonProfessionalOrganization,
   * == Pro ==
   * 6 = ProfessionalStandalone,
   * == Org-Pro ==
   * 8 = ProfessionalOrganization,
   * == Org Admin ==
   * 9 = ProfessionalOrganizationAdmin,
   * == Reviewer ==
   * 10 = ProfessionalEcgReviewer
   * == Consultant ==
   * 11 = Consultant
   * == Undefined ==
   * 1 = Patient (Legacy),
   * 2 = Physician (Legacy),
   * 3 = Nurse (Legacy),
   * 4 = ApiUser (Legacy),
   */
  switch (role) {
    case Role.NonProfessional:
      return 5;
    case Role.OrgNonProfessional:
      return 7;
    case Role.Professional:
    case Role.OrgProfessional:
      return 8;
    case Role.OrgAdmin:
      return 9;
    case Role.Reviewer:
      return 10;
    case Role.Consultant:
      return 11;
    default:
      throw new Error(
        `Error converting App Role to Server Role. Invalid App Role ${role}`
      );
  }
};

type GetPermissionsFromServerPermissionsArgs = {
  canViewEcgs: boolean,
  canEditInterpretation: boolean,
  canManageNonProUsers: boolean,
  canManageEntities?: boolean,
  canViewReports: boolean,
  canLogIntoPortal: boolean,
  canApproveEcg: boolean,
  canViewSelfClinicsEcgs?: boolean,
  canSendEcgsViaMail?: boolean,
  showEcgPreview?: boolean,
  canManageProUsers?: boolean,
  canArchiveEcgs?: boolean,
  isViewSelfClinicIsAllowed?: boolean
}

export const getPermissionsFromServerPermissions = (
  permissionsObj: GetPermissionsFromServerPermissionsArgs
) => {
  let permissions = [];
  const { isViewSelfClinicIsAllowed, canApproveEcg, canEditInterpretation, canLogIntoPortal, canManageNonProUsers, canViewEcgs, canViewReports, canSendEcgsViaMail, canManageProUsers, canViewSelfClinicsEcgs, showEcgPreview, canArchiveEcgs, canManageEntities } = permissionsObj

  if (isViewSelfClinicIsAllowed) {
    permissions.push(Permissions.IsViewSelfClinicIsAllowed);
  }
  if (canArchiveEcgs) {
    permissions.push(Permissions.CanArchiveEcgs);
  }
  if (canSendEcgsViaMail) {
    permissions.push(Permissions.CanSendEcgsViaMail);
  }
  if (canViewSelfClinicsEcgs) {
    permissions.push(Permissions.CanViewSelfClinicsEcgs);
  }
  if (showEcgPreview) {
    permissions.push(Permissions.ShowEcgPreview);
  }

  if (canViewEcgs) {
    permissions.push(Permissions.CanViewEcgs);
  }

  if (canEditInterpretation) {
    permissions.push(Permissions.CanEditInterpretation);
  }

  if (canManageNonProUsers) {
    permissions.push(Permissions.CanManageNonProUsers);
  }

  if (canManageProUsers) {
    permissions.push(Permissions.CanManageProUsers);
  }

  if (canManageEntities) {
    permissions.push(Permissions.CanManageEntities);
  }

  if (canViewReports) {
    permissions.push(Permissions.CanViewReports);
  }

  if (canLogIntoPortal) {
    permissions.push(Permissions.CanLogIntoPortal);
  }

  if (canApproveEcg) {
    permissions.push(Permissions.CanApproveEcg);
  }

  return permissions;
};

export const getLoginFailureReason = (
  errorCode: string
): LoginFailureReason => {
  switch (errorCode) {
    case ErrorConstants.PASSWORD_EXPIRED_CODE:
      return LoginFailureReason.PasswordExpired;
    case ErrorConstants.LOGIN_BLOCKED_CODE:
      return LoginFailureReason.Blocked;
    case ErrorConstants.LOGIN_INVALID_CREDENTIALS_CODE:
      return LoginFailureReason.InvalidCredentials;
    default:
      return LoginFailureReason.Unknown;
  }
};

export const isLoginRoute = (route?: string) =>
  route?.indexOf(RouteConstants.LOGIN) !== -1;

export const isChangePasswordRoute = (route?: string) =>
  route?.indexOf(RouteConstants.CHANGE_PASSWORD) !== -1;

export const isResetPasswordRoute = (route?: string) =>
  route?.indexOf(RouteConstants.RESET_PASSWORD) !== -1;

export const isViewEcgRoute = (route?: string) =>
  route?.indexOf(RouteConstants.VIEW_ECG) !== -1;

export const isViewPrescriptionRoute = (route?: string) =>
  route?.indexOf(RouteConstants.VIEW_PRESCRIPTION) !== -1;

export const shouldLogoutOnUnauthorized = (pathName: string): boolean => !isLoginRoute(pathName) &&
  !isChangePasswordRoute(pathName) &&
  !isViewEcgRoute(pathName) &&
  !isViewPrescriptionRoute(pathName) &&
  !isResetPasswordRoute(pathName)

export const shouldAttachAccessToken = (pathName: string): boolean => !isLoginRoute(pathName) && !isViewPrescriptionRoute(pathName)

export const getUserFromToken = (token: string | null): ITokenUser | null => {
  logger.info(getUserFromToken.name, "Trying to extract User from Token..");

  if (!token) {
    logger.error(getUserFromToken.name, "No token provided");
    return null;
  }

  const decodedToken = decodeToken(token) as IToken;

  if (!decodedToken) {
    logger.error(getUserFromToken.name, "Unable to decode token.");
    return null;
  }

  try {
    const tokenUser = decodedToken.shUser
      ? ({ UserName: decodedToken.shUser } as ITokenUser)
      : (JSON.parse(decodedToken.lu) as ITokenUser);
    logger.info(getUserFromToken.name, "User extracted from token:", tokenUser);

    return tokenUser;
  } catch (error) {
    logger.error(getUserFromToken.name, "Unable to extract user from token.");
    return null;
  }
};

export const getLogoutUrlFromToken = (logoutReason: LogoutReason): string => {
  const token = LocalStorageUtil.get(Constants.JWT_KEY);
  const user = getUserFromToken(token);

  return getLogoutUrl(logoutReason, {
    isSso: user?.IsSso,
    signInUrl: user?.SignInUrl,
    signOutUrl: user?.SignOutUrl,
  });
};

export const getLogoutUrl = (
  logoutReason: LogoutReason,
  opts: {
    isSso?: boolean;
    signInUrl?: string;
    signOutUrl?: string;
  }
) => {
  const { isSso = false, signInUrl, signOutUrl } = opts;

  let logoutUrl;
  if (isSso) {
    logoutUrl = logoutReason === "manual" ? signOutUrl : signInUrl;
  }

  return logoutUrl || RouteConstants.LOGIN_PATH;
};

export const mapTokenUserToCreateAuthUser = (
  tokenUser: ITokenUser
): ICreateAuthenticatedUser => {
  const {
    UserId,
    UserName,
    FirstName,
    LastName,
    RoleID,
    Organization,
    Prefix,
    ClientAccessUserPermission,
    CanViewEcgs,
    CanEditInterpretation,
    CanViewReports,
    CanManageNonProUsers,
    CanManageProUsers,
    CanManageEntities,
    CanLogIntoPortal,
    CanApproveEcg,
    IsSso,
    SignInUrl,
    SignOutUrl,
  } = tokenUser;

  const createAuthUser: ICreateAuthenticatedUser = {
    id: UserId,
    email: UserName ?? "",
    firstName: FirstName ?? "",
    lastName: LastName ?? "",
    title: Prefix,
    serverRoleId: RoleID,
    organization: Organization
      ? {
        id: Organization.OrganizationId?.toString(),
        name: Organization.OrganizationName,
        type: Organization.OrganizationType,
      }
      : undefined,
    canViewEcgs: CanViewEcgs,
    canEditInterpretation: CanEditInterpretation,
    canViewReports: CanViewReports,
    canManageNonProUsers: CanManageNonProUsers,
    canManageProUsers: CanManageProUsers,
    canManageEntities: CanManageEntities,
    canLogIntoPortal: CanLogIntoPortal,
    canApproveEcg: CanApproveEcg,
    clientAccessUserPermission: ClientAccessUserPermission,
    isSso: IsSso,
    signInUrl: SignInUrl,
    signOutUrl: SignOutUrl,
    isViewSelfClinicIsAllowed: Organization.IsViewSelfClinicIsAllowed
  };

  return createAuthUser;
};

export const mapServerUserToCreateAuthUser = (
  user: FullSmartheartUser
): ICreateAuthenticatedUser => {
  const {
    id,
    userName,
    firstName,
    lastName,
    roleId,
    salutation,
    organization,
    canViewEcgs,
    canEditInterpretation,
    canViewReports,
    canManageNonProUsers,
    canManageProUsers,
    canManageEntities,
    canLogIntoPortal,
    canApproveEcg,
    clientAccessUserPermission,
    isSso,
    signInUrl,
    signOutUrl,
  } = user;

  const createAuthUser: ICreateAuthenticatedUser = {
    id,
    email: userName,
    firstName,
    lastName,
    serverRoleId: roleId,
    title: salutation,
    organization: organization
      ? {
        id: organization.organizationId.toString() ?? "",
        name: organization.organizationName ?? "",
        type: organization.organizationType ?? OrgType.Undefined,
      }
      : undefined,
    canViewEcgs,
    canEditInterpretation,
    canViewReports,
    canManageNonProUsers,
    canManageProUsers,
    canManageEntities,
    canLogIntoPortal,
    canApproveEcg,
    clientAccessUserPermission,
    isSso,
    signInUrl,
    signOutUrl,
  };

  return createAuthUser;
};
